import React, { useState, useEffect, useRef } from "react";
import { useChanges, useEditorData } from "./AdminDashboard";
import { fetchSection } from "../../services/api";
import ptBRData from "../../locales/pt-BR.json";
import enUSData from "../../locales/en-US.json";
import esESData from "../../locales/es-ES.json";
import "./Admin.css";

interface LocaleData {
  [key: string]: any;
}

const LocalesEditor: React.FC = () => {
  const { addChange, clearChange, savedSignal } = useChanges();
  const { registerEditor, unregisterEditor } = useEditorData();
  const [locales, setLocales] = useState<{ [key: string]: LocaleData }>({});
  const [originalLocales, setOriginalLocales] = useState<{
    [key: string]: LocaleData;
  }>({});
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState<"pt-BR" | "en-US" | "es-ES">(
    "pt-BR"
  );
  const [searchTerm, setSearchTerm] = useState("");

  const localeKeys = React.useMemo(
    () => ["pt-BR", "en-US", "es-ES"] as const,
    []
  );

  useEffect(() => {
    // Load the live locales from the worker, falling back to the bundled copies.
    let cancelled = false;
    (async () => {
      const [ptBR, enUS, esES] = await Promise.all([
        fetchSection<LocaleData>("locale-pt-BR", ptBRData as LocaleData),
        fetchSection<LocaleData>("locale-en-US", enUSData as LocaleData),
        fetchSection<LocaleData>("locale-es-ES", esESData as LocaleData),
      ]);
      if (cancelled) return;
      const loadedLocales = {
        "pt-BR": ptBR,
        "en-US": enUS,
        "es-ES": esES,
      };
      setLocales(loadedLocales);
      setOriginalLocales(JSON.parse(JSON.stringify(loadedLocales)));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Rebaseline after a successful save (no reload -> no stale KV read-back).
  const localesRef = useRef(locales);
  localesRef.current = locales;
  useEffect(() => {
    if (savedSignal > 0) {
      setOriginalLocales(JSON.parse(JSON.stringify(localesRef.current)));
    }
  }, [savedSignal]);

  useEffect(() => {
    // Track changes by comparing current state with original
    if (loading) return;

    localeKeys.forEach((locale) => {
      const current = locales[locale];
      const original = originalLocales[locale];
      if (!current || !original) return;

      if (JSON.stringify(current) !== JSON.stringify(original)) {
        addChange({
          id: `locale-${locale}`,
          section: "Traduções",
          type: "edit",
          description: `Alterar traduções: ${locale}`,
        });
      } else {
        // Edited back to the original value — drop any stale pending change.
        clearChange(`locale-${locale}`);
      }
    });
  }, [locales, originalLocales, localeKeys, addChange, clearChange, loading]);

  // Register data getter for AdminDashboard
  useEffect(() => {
    const getData = () =>
      localeKeys.map((locale) => ({
        section: `locale-${locale}`,
        data: locales[locale],
      }));
    registerEditor("Traduções", getData);
    return () => unregisterEditor("Traduções");
  }, [locales, localeKeys, registerEditor, unregisterEditor]);

  const updateValue = (keys: string[], value: string) => {
    const newLocales = { ...locales };
    let current = newLocales[activeLang];

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setLocales(newLocales);
  };

  const renderFields = (
    obj: any,
    prefix: string[] = []
  ): React.ReactElement[] => {
    const fields: React.ReactElement[] = [];

    Object.keys(obj).forEach((key) => {
      const newPath = [...prefix, key];
      const path = newPath.join(".");

      // Filter based on search term
      if (searchTerm && !path.toLowerCase().includes(searchTerm.toLowerCase()) &&
          (typeof obj[key] !== 'string' || !obj[key].toLowerCase().includes(searchTerm.toLowerCase()))) {
        // If it's an object, check if any child matches
        if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
          const childFields = renderFields(obj[key], newPath);
          if (childFields.length > 0) {
            fields.push(
              <div
                key={path}
                style={{ marginLeft: `${prefix.length * 20}px`, marginTop: "10px" }}
              >
                <strong>{key}:</strong>
              </div>
            );
            fields.push(...childFields);
          }
        }
        return;
      }

      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        fields.push(
          <div
            key={path}
            style={{ marginLeft: `${prefix.length * 20}px`, marginTop: "10px" }}
          >
            <strong>{key}:</strong>
          </div>
        );
        fields.push(...renderFields(obj[key], newPath));
      } else {
        const isHighlighted = searchTerm &&
          (path.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (typeof obj[key] === 'string' && obj[key].toLowerCase().includes(searchTerm.toLowerCase())));

        fields.push(
          <div
            key={path}
            className="form-group"
            style={{
              marginLeft: `${prefix.length * 20}px`,
              backgroundColor: isHighlighted ? '#fff3cd' : 'transparent',
              padding: isHighlighted ? '10px' : '0',
              borderRadius: isHighlighted ? '6px' : '0',
              marginBottom: '10px'
            }}
          >
            <label>{path}</label>
            <input
              type="text"
              value={obj[key] || ""}
              onChange={(e) => updateValue(newPath, e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
        );
      }
    });

    return fields;
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="editor-container">
      <div className="editor-header">
        <h2>Gerenciar Traduções</h2>
        <div className="form-group" style={{ marginBottom: 0, maxWidth: '400px' }}>
          <input
            type="text"
            placeholder="Buscar chave ou valor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px' }}
          />
        </div>
      </div>

      <div className="language-tabs">
        {localeKeys.map((locale) => (
          <button
            key={locale}
            className={`language-tab ${activeLang === locale ? "active" : ""}`}
            onClick={() => setActiveLang(locale)}
          >
            {locale}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {locales[activeLang] && renderFields(locales[activeLang])}
      </div>
    </div>
  );
};

export default LocalesEditor;
